import BpkSelectableChip from '@skyscanner-internal/backpack-web/bpk-component-chip';
import BpkMultiSelectChipGroup, {
  CHIP_GROUP_TYPES,
  CHIP_COMPONENT,
} from '@skyscanner-internal/backpack-web/bpk-component-chip-group';
import BpkSelect from '@skyscanner-internal/backpack-web/bpk-component-select';
import BpkText, {
  TEXT_STYLES,
} from '@skyscanner-internal/backpack-web/bpk-component-text';

import getFeatureDecision from '../../../../config';
import { ENABLE_DEPARTURE_TIMES } from '../../../../config/constants';
import { useLocalisation } from '../../../../services/contextAdaptor/LocalisationContext';
import { ALL_DEPARTURE_TIMES } from '../../helpers/constants/departureTimeConstants';
import { useTranslation } from '../../helpers/hooks/useTranslation';
import { formatDepartureTimeRange } from '../../helpers/presentation/formatDepartureTimeRange';

import './DepartureTimesFilterView.scss';

import type { DepartureTime } from '../../helpers/constants/departureTimeConstants';
import type { ChipRenderProps } from '@skyscanner-internal/backpack-web/bpk-component-chip-group';

type Props = Readonly<{
  selectedTimes?: DepartureTime[];
  onChange: (
    event: React.ChangeEvent<HTMLSelectElement>,
    multi?: boolean,
  ) => void;
}>;

const DEPARTURE_TIME_TRANSLATION_KEYS: Record<DepartureTime, string> = {
  EARLY_MORNING: 'mvp_departure_early',
  MORNING: 'mvp_departure_morning',
  AFTERNOON: 'mvp_departure_afternoon',
  EVENING: 'mvp_departure_evening',
};

const NO_SELECTED_TIMES: DepartureTime[] = [];

export const DepartureTimesFilterView = ({
  onChange,
  selectedTimes = NO_SELECTED_TIMES,
}: Props) => {
  const { formatTime } = useLocalisation();
  const translate = useTranslation();
  const departureTimesVariant = getFeatureDecision(ENABLE_DEPARTURE_TIMES, 'A');
  const singleSelect = departureTimesVariant === 'B';
  const selectedValue =
    selectedTimes.length === 0 ? 'ANYTIME' : selectedTimes[0];

  const departureTimeOptions = [
    <option key="ANYTIME" value="ANYTIME">
      {translate('mvp_departure_any_time')}
    </option>,
    ...ALL_DEPARTURE_TIMES.map((time) => (
      <option key={time} value={time}>
        {translate(DEPARTURE_TIME_TRANSLATION_KEYS[time])} (
        {formatDepartureTimeRange(time, { formatTime })})
      </option>
    )),
  ];

  const chips = ALL_DEPARTURE_TIMES.map((time) => ({
    text: translate(DEPARTURE_TIME_TRANSLATION_KEYS[time]),
    selected: selectedTimes.includes(time),
    component: CHIP_COMPONENT.selectable,
    onClick: () =>
      onChange(
        { target: { value: time } } as React.ChangeEvent<HTMLSelectElement>,
        true,
      ),
    renderChip: (chipProps: ChipRenderProps) => (
      <BpkSelectableChip
        className="departure-time-chip"
        accessibilityLabel={chipProps.accessibilityLabel}
        selected={chipProps.selected}
        type={chipProps.type}
        role={chipProps.role}
        onClick={() => chipProps.onClick()}
      >
        <span className="departure-time-chip__content">
          <BpkText textStyle={TEXT_STYLES.label2}>
            {translate(DEPARTURE_TIME_TRANSLATION_KEYS[time])}
          </BpkText>
          <BpkText textStyle={TEXT_STYLES.caption}>
            {formatDepartureTimeRange(time, { formatTime })}
          </BpkText>
        </span>
      </BpkSelectableChip>
    ),
  }));

  return (
    <>
      {singleSelect ? (
        <BpkSelect
          id="departure-time"
          name="departure-time"
          aria-label={translate('mvp_departure_time')}
          onChange={onChange}
          value={selectedValue}
        >
          {departureTimeOptions}
        </BpkSelect>
      ) : (
        <div className="departure-time-chips">
          <BpkMultiSelectChipGroup
            type={CHIP_GROUP_TYPES.wrap}
            chips={chips}
            ariaLabel={translate('mvp_departure_time')}
          />
        </div>
      )}
    </>
  );
};
